import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import {
  Trophy,
  Gift,
  Ticket,
  User,
  Calendar,
  CheckCircle,
  XCircle,
  RefreshCw,
  Play,
  AlertTriangle,
  PartyPopper,
  Home,
  Tractor,
  Bike,
  Laptop,
  Tv
} from 'lucide-react';
import { API_URL as API } from '../../config/api';

const PRIZE_ICONS = {
  house: Home,
  tractor: Tractor,
  tricycle: Bike,
  laptop: Laptop,
  tv: Tv
};

const DrawManager = () => {
  const [drawStatus, setDrawStatus] = useState(null);
  const [winners, setWinners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drawing, setDrawing] = useState(false);
  const [selectedPrize, setSelectedPrize] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);

  const token = localStorage.getItem('session_token');
  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    loadDrawStatus();
    loadWinners();
  }, []);

  const loadDrawStatus = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/admin/draw/status`, {
        headers,
        withCredentials: true
      });
      setDrawStatus(response.data);
    } catch (error) {
      console.error('Error loading draw status:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const loadWinners = async () => {
    try {
      const response = await axios.get(`${API}/admin/winners`, {
        headers,
        withCredentials: true
      });
      setWinners(response.data.winners || []);
    } catch (error) {
      console.error('Error loading winners:', error);
    }
  };

  const handleDrawClick = (prizeType) => {
    setSelectedPrize(prizeType);
    setShowConfirm(true);
  };

  const executeDraw = async () => {
    if (!selectedPrize) return;

    setDrawing(true);
    setShowConfirm(false);

    try {
      const response = await axios.post(`${API}/admin/draw/execute`, 
        { prize_type: selectedPrize },
        { headers, withCredentials: true }
      );

      toast.success(
        <div className="flex items-center gap-2">
          <PartyPopper className="w-5 h-5 text-yellow-500" />
          <span>Gagnant: {response.data.winner.user_name}</span>
        </div>
      );

      loadDrawStatus();
      loadWinners();
    } catch (error) {
      console.error('Error executing draw:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors du tirage');
    } finally {
      setDrawing(false);
      setSelectedPrize(null);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="space-y-6"
    >
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-bold flex items-center gap-2">
            <Trophy className="w-5 h-5 text-yellow-500" />
            Tirage au Sort
          </h2>
          <p className="text-gray-500 text-sm">
            {drawStatus?.total_active_tickets || 0} billets actifs participent au tirage
          </p>
        </div>
        <Button variant="outline" onClick={loadDrawStatus}>
          <RefreshCw className="w-4 h-4 mr-1" /> Actualiser
        </Button>
      </div>

      {/* Warning Banner */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
        <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="font-medium text-amber-800">Attention - Action irréversible</p>
          <p className="text-sm text-amber-700">
            Une fois le tirage effectué pour un prix, il ne peut pas être annulé. 
            Assurez-vous que tous les billets sont vendus et validés avant de procéder.
          </p>
        </div>
      </div>

      {/* Prize Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {drawStatus?.prizes && Object.entries(drawStatus.prizes).map(([prizeType, prize]) => {
          const Icon = PRIZE_ICONS[prizeType] || Gift;
          const isDrawn = prize.drawn;
          
          return (
            <div 
              key={prizeType}
              className={`bg-white rounded-2xl shadow-sm overflow-hidden border-2 ${
                isDrawn ? 'border-green-200' : 'border-gray-100'
              }`}
            >
              <div className={`p-4 ${isDrawn ? 'bg-green-50' : 'bg-gray-50'}`}>
                <div className="flex items-center justify-between">
                  <div className={`p-2 rounded-lg ${isDrawn ? 'bg-green-100' : 'bg-white'}`}>
                    <Icon className={`w-6 h-6 ${isDrawn ? 'text-green-600' : 'text-gray-600'}`} />
                  </div>
                  {isDrawn ? (
                    <span className="flex items-center gap-1 text-sm text-green-600 font-medium">
                      <CheckCircle className="w-4 h-4" /> Tiré
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 text-sm text-gray-400">
                      <XCircle className="w-4 h-4" /> En attente
                    </span>
                  )}
                </div>
              </div>
              
              <div className="p-4">
                <h3 className="font-bold text-lg">{prize.name}</h3>
                <p className="text-2xl font-bold text-primary">{formatCurrency(prize.value)}</p>
                
                {isDrawn && prize.winner ? (
                  <div className="mt-4 p-3 bg-green-50 rounded-lg">
                    <p className="text-sm font-medium text-green-800 flex items-center gap-1">
                      <Trophy className="w-4 h-4" /> Gagnant
                    </p>
                    <p className="font-bold text-green-900 mt-1">{prize.winner.user_name || 'N/A'}</p>
                    <p className="text-sm text-green-700 flex items-center gap-1 mt-1">
                      <Ticket className="w-3 h-3" />
                      {prize.winner.ticket_number}
                    </p>
                    <p className="text-xs text-green-600 mt-1">
                      {formatDate(prize.winner.draw_date)}
                    </p>
                  </div>
                ) : (
                  <Button 
                    onClick={() => handleDrawClick(prizeType)}
                    className="w-full mt-4 bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600"
                    disabled={drawing}
                  >
                    <Play className="w-4 h-4 mr-2" />
                    Effectuer le tirage
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Confirmation Modal */}
      {showConfirm && selectedPrize && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div 
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            className="bg-white rounded-2xl p-6 max-w-md w-full"
          >
            <div className="text-center">
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Trophy className="w-8 h-8 text-yellow-600" />
              </div>
              <h3 className="text-xl font-bold mb-2">Confirmer le tirage</h3>
              <p className="text-gray-600 mb-4">
                Vous êtes sur le point d'effectuer le tirage pour le prix :
              </p>
              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <p className="font-bold text-lg">
                  {drawStatus?.prizes?.[selectedPrize]?.name}
                </p>
                <p className="text-2xl font-bold text-primary">
                  {formatCurrency(drawStatus?.prizes?.[selectedPrize]?.value || 0)}
                </p>
              </div>
              <p className="text-sm text-amber-600 mb-6">
                ⚠️ Cette action est irréversible
              </p>
              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => { setShowConfirm(false); setSelectedPrize(null); }}
                >
                  Annuler
                </Button>
                <Button 
                  className="flex-1 bg-gradient-to-r from-yellow-500 to-orange-500"
                  onClick={executeDraw}
                  disabled={drawing}
                >
                  {drawing ? (
                    <RefreshCw className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Play className="w-4 h-4 mr-2" />
                  )}
                  Confirmer
                </Button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {/* Winners History */}
      {winners.length > 0 && (
        <div className="bg-white rounded-2xl shadow-sm">
          <div className="p-4 border-b">
            <h3 className="font-bold flex items-center gap-2">
              <Trophy className="w-5 h-5 text-yellow-500" />
              Historique des Gagnants
            </h3>
          </div>
          <div className="divide-y">
            {winners.map((winner) => {
              const Icon = PRIZE_ICONS[winner.prize_type] || Gift;
              return (
                <div key={winner.winner_id} className="p-4 flex items-center gap-4">
                  <div className="p-2 bg-yellow-50 rounded-lg">
                    <Icon className="w-5 h-5 text-yellow-600" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold">{winner.prize_name}</p>
                    <p className="text-sm text-gray-500">
                      {winner.user_name} • {winner.ticket_number}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-primary">
                      {formatCurrency(winner.prize_value)}
                    </p>
                    <p className="text-xs text-gray-400">
                      {formatDate(winner.draw_date)}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default DrawManager;
